import {useEffect} from 'react'
import api from '../../../common/api.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'
import NumberInput from '../../../common/components/Form/NumberInput.js'
import {showConfirmMessageModal} from '../../../common/components/Modal/ConfirmMessageModal.js'
import TextInput from '../../../common/components/TextInput.js'
import {showGlobalError} from '../../GlobalErrorMessage.js'
import SettingsPanel from '../SettingsPanel.js'
import {
  formSelector,
  getState,
  onlyIfAutoForm,
  updateForm,
  useSelector,
} from '../../../store.js'
import Zelect from '../../../common/components/Zelect.js'
import {isPositiveInteger, isPresent} from '../../../common/utils.js'
import {settingsPermissionsSelector} from '../settingsFunctions.js'
import {PURCHASE_ORDERS} from '../../../common/constants/SettingsPanels.js'

const PURCHASE_ORDER_SETTINGS = 'purchase_order_settings'
const DATE_TIME_SEQUENCE = 'DATE_TIME_SEQUENCE'
const NUMBER_SEQUENCE = 'NUMBER_SEQUENCE'
const DEFAULT_PREFIX = 'PO-'
const DEFAULT_START_SEQUENCE = '100000'

const SEQUENCE_OPTIONS = [
  {value: DATE_TIME_SEQUENCE, display: 'Date/Time Sequence'},
  {value: NUMBER_SEQUENCE, display: 'Sequential Numbers'},
]

function formSetupFunction() {
  return {
    formName: PURCHASE_ORDER_SETTINGS,
    initialForm: {poSettings: null},
  }
}

async function showPOIDNumberingModal() {
  const {poSettings} = formSelector(getState(), {
    formName: PURCHASE_ORDER_SETTINGS,
  })

  await showConfirmMessageModal({
    title: 'Configure PO ID Format',
    confirmText: 'Save',
    cancelText: 'Cancel',
    Message({form, setModalFormValue, errors}) {
      return (
        <ul className="list list--no-style">
          <li className="list__item margin-bottom-15">
            <Zelect
              value={form.confirmValue.type}
              onChange={(option) =>
                setModalFormValue(['confirmValue', 'type'], option.value)
              }
              options={SEQUENCE_OPTIONS}
            />
          </li>
          {form.confirmValue.type === NUMBER_SEQUENCE && (
            <>
              <li className="list__item w-5rem margin-bottom-15">
                <TextInput
                  id="prefix"
                  label="Prefix"
                  value={form.confirmValue.prefix || ''}
                  onChange={(value) =>
                    setModalFormValue(['confirmValue', 'prefix'], value)
                  }
                  placeholder={DEFAULT_PREFIX}
                />
              </li>
              <li className="list__item margin-bottom-0">
                <NumberInput
                  containerClassName="w-60"
                  id="sequence"
                  label={
                    poSettings ? 'Next Number in Sequence' : 'Start of Sequence'
                  }
                  value={form.confirmValue.sequence || ''}
                  onChange={(value) =>
                    setModalFormValue(['confirmValue', 'sequence'], value)
                  }
                  placeholder={DEFAULT_START_SEQUENCE}
                  minWidth="7rem"
                  errorMessage={errors.sequence}
                />
              </li>
            </>
          )}
        </ul>
      )
    },
    errorsSelector(state, {formName}) {
      const {
        confirmValue: {type, sequence},
      } = formSelector(state, {formName})
      const errors = {}

      // default values are used if prefix and sequence aren't provided
      // so values are not required

      if (type === NUMBER_SEQUENCE) {
        if (isPresent(sequence) && !isPositiveInteger(sequence)) {
          errors.sequence = 'Sequence must be a positive whole number'
          errors.preventSave = true
        }
      }

      return errors
    },
    confirmValue: {
      type: poSettings ? NUMBER_SEQUENCE : DATE_TIME_SEQUENCE,
      prefix: poSettings?.prefix,
      sequence: poSettings?.sequence,
    },
    async onResolve({outcome}) {
      if (!outcome) {
        return
      }

      let {type, prefix, sequence} = outcome

      prefix ||= DEFAULT_PREFIX
      sequence ||= DEFAULT_START_SEQUENCE

      const action =
        type === NUMBER_SEQUENCE
          ? poSettings
            ? 'put'
            : 'post'
          : poSettings
            ? 'delete'
            : null

      if (!action) {
        return
      }

      const params = {prefix, sequence: Number(sequence)}

      const {json} = await api[action](
        '/company/settings/purchase_order/',
        action === 'delete' ? undefined : params,
      )

      updateForm(PURCHASE_ORDER_SETTINGS, {
        poSettings: action === 'delete' ? null : json,
      })
    },
  })
}

async function getPOSettings() {
  try {
    const {json: poSettings} = await api.get(
      '/company/settings/purchase_order/',
    )

    updateForm(PURCHASE_ORDER_SETTINGS, {poSettings})
  } catch (err) {
    if (err.response?.status !== 404) {
      showGlobalError(
        {
          summary: 'Unable to get Purchase Order settings',
          details: err.message,
        },
        err,
      )
    }
  }
}

function PurchaseOrders({form}) {
  useEffect(() => {
    getPOSettings()
  }, [])

  const hasPermission = useSelector(settingsPermissionsSelector)[
    PURCHASE_ORDERS
  ]

  return (
    <SettingsPanel
      header="Purchase Order Settings"
      subHeader={<hr className="margin-bottom-0" />}
      hasPermission={hasPermission}
    >
      <div className="row margin-bottom-10">
        <div className="medium-7 columns">
          <dl className="list list--no-style">
            <dt className="list__title fs-02 lh-md margin-bottom-10">
              <strong className="margin-right-7">
                Purchase Order ID Format:
              </strong>
              {form.poSettings ? (
                <span className="unbold">Sequential Numbers</span>
              ) : (
                <span className="unbold">Date/Time-Based</span>
              )}
            </dt>
            {form.poSettings ? (
              <>
                <dd className="list__item">
                  <strong>ID Prefix: </strong> {form.poSettings.prefix}
                </dd>
                <dd className="list__item margin-bottom-15">
                  <strong>Next Number in the Sequence: </strong>
                  {form.poSettings.sequence}
                </dd>
              </>
            ) : (
              <dd className="list__item margin-bottom-15">
                <strong>Example: </strong>
                20230227-104629425
              </dd>
            )}
            <dd className="list__item">
              <ButtonPrimary
                isOutlined
                size="sm"
                onClick={showPOIDNumberingModal}
              >
                Configure Format
              </ButtonPrimary>
            </dd>
          </dl>
        </div>
      </div>
    </SettingsPanel>
  )
}

export default onlyIfAutoForm(PurchaseOrders, formSetupFunction)
